import React from "react";
import { type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Brand-aligned empty state.
 *
 * - Icon sits in a Sand-raised ring with Forest-tinted icon color
 * - Title uses DM Serif Display (font-display) per brand voice
 *   ("Display / Headlines")
 * - Optional CTA uses Burnt Orange per spec ("CTAs, primary buttons")
 *
 * Voice on copy is the caller's responsibility — keep it short, direct,
 * no exclamation marks. See HollerHub Brand Guidelines §07.
 */
export function EmptyState({ icon: Icon, title, description, actionLabel, onAction }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-raised dark:bg-surface-raised-dark border border-border mb-4">
        <Icon className="h-8 w-8 text-forest dark:text-forest-dark" />
      </div>
      <h3 className="font-display text-2xl text-charcoal dark:text-charcoal-dark mb-1">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
        {description}
      </p>
      {actionLabel && onAction && (
        <Button
          onClick={onAction}
          className="mt-5 bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
